import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { checkStatusCode } from '../../../shared/helpers/checkStatusCode';
import { SineHeader } from '../../../shared/types';
import type DeviceService from './device.service';

const DEVICEID_ERROR = 'DEVICEID_ERROR';

const MAX_DEVICE_ATTEMPTS = 2;

interface IDeviceAxiosConfig extends AxiosRequestConfig {
  attempts?: number;
}

export default class DeviceInterceptor {
  private clientServiceInstance: DeviceService;
  private axiosInstance: AxiosInstance;
  private axiosResponseSubscriptionId?: number;
  private axiosRequestSubscriptionId?: number;

  /**
   * Create a new DeviceInterceptor, startIntercepting needs to be called before any requests/responses will be acted on.
   * @param axiosInstance The axios instance through which requests should be intercepted.
   * @param deviceServiceInstance The instance of the device service used to get the device id to add to requests and to clear the device id if the server reports it invalid.
   */
  constructor(axiosInstance: AxiosInstance, deviceServiceInstance: DeviceService) {
    this.axiosInstance = axiosInstance;
    this.clientServiceInstance = deviceServiceInstance;

    this.startIntercepting = this.startIntercepting.bind(this);
    this.stopIntercepting = this.stopIntercepting.bind(this);
    this.handleAxiosError = this.handleAxiosError.bind(this);
  }

  public startIntercepting() {
    this.axiosResponseSubscriptionId = this.axiosInstance.interceptors.response.use(
      (axiosResponse) => axiosResponse,
      this.handleAxiosError,
    );
    this.axiosRequestSubscriptionId = this.axiosInstance.interceptors.request.use(
      (axiosRequest) => {
        const currentDeviceToken = this.clientServiceInstance.getDeviceToken();

        axiosRequest.headers[SineHeader.X_SINE_DEVICE_ID] = currentDeviceToken ?? '';

        return axiosRequest;
      },
      (error) => error,
    );
  }

  public stopIntercepting() {
    if (this.axiosResponseSubscriptionId) {
      this.axiosInstance.interceptors.response.eject(this.axiosResponseSubscriptionId);
    }
    if (this.axiosRequestSubscriptionId) {
      this.axiosInstance.interceptors.request.eject(this.axiosRequestSubscriptionId);
    }
  }

  private async handleAxiosError(error: any) {
    if (checkStatusCode(error, 401, DEVICEID_ERROR)) {
      this.clientServiceInstance.trashDeviceToken();

      const config: IDeviceAxiosConfig = error.response.config;
      config.attempts = (config.attempts || 0) + 1;
      if (config.attempts > MAX_DEVICE_ATTEMPTS) {
        return Promise.reject(new Error('Too many register device attempts'));
      }

      await this.clientServiceInstance.register();

      return this.axiosInstance(config);
    }

    return Promise.reject(error);
  }
}
